import { Injectable, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@environment';

import { LANGUAGES } from '@core/constants/language.constants';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private supportedLangs: string[] = Object.values(LANGUAGES).filter(
    lang => lang !== LANGUAGES.ZZ,
  );

  private readonly _currentLanguage = signal<string>(LANGUAGES.EN);
  readonly currentLanguage = this._currentLanguage.asReadonly();

  constructor(
    private translateService: TranslateService,
    private appTitle: Title,
  ) {
    if (environment.development) {
      this.supportedLangs.push(LANGUAGES.ZZ);
    }
  }

  setMemberLanguage(memberLang: string, companyDefaultLang: string): void {
    if (memberLang && this.supportedLangs.includes(memberLang)) {
      this._currentLanguage.set(memberLang);
    } else {
      const browserLang = this.translateService.getBrowserLang();
      if (browserLang && this.supportedLangs.includes(browserLang)) {
        this._currentLanguage.set(browserLang);
      } else if (
        companyDefaultLang &&
        this.supportedLangs.includes(companyDefaultLang)
      ) {
        this._currentLanguage.set(companyDefaultLang);
      }
    }

    this.setLanguage();
  }

  setLanguage(): void {
    this.translateService.addLangs(this.supportedLangs);
    this.translateService.setDefaultLang(
      environment.development ? LANGUAGES.ZZ : LANGUAGES.EN,
    );

    this.translateService
      .use(this._currentLanguage())
      .pipe(take(1))
      .subscribe(() =>
        this.appTitle.setTitle(this.translateService.instant('app.title')),
      );

    // @ts-ignore
    window.WEBSPELLCHECKER_CONFIG.localization = this._currentLanguage();
  }

  getSupportedLanguages(): string[] {
    return this.supportedLangs;
  }
}
